import { Component, type ReactNode, type ErrorInfo } from 'react';
import * as Sentry from '@sentry/react';
import './ErrorBoundary.css';
import { Button } from './Button';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackMessage?: string;
  onReset?: () => void;  // Optional callback when user clicks "Try Again"
  showRefreshButton?: boolean;  // Default: true
  showErrorDetails?: boolean;  // Default: true (in development)
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });

    // Send error to Sentry with React component stack
    Sentry.captureException(error, {
      level: 'error',
      tags: {
        error_category: 'ui_error',
        error_boundary: 'true',
      },
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      fingerprint: ['react-error-boundary', error.name],
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });

    // Call optional onReset callback
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      const {
        fallbackTitle = 'Something went wrong',
        fallbackMessage = 'An unexpected error occurred. Please try refreshing the page.',
        showRefreshButton = true,
        showErrorDetails = import.meta.env.DEV,
      } = this.props;

      return (
        <div className="error-boundary">
          <div className="error-boundary__content">
            <div className="error-boundary__icon">⚠️</div>
            <h2>{fallbackTitle}</h2>
            <p>{fallbackMessage}</p>

            {showErrorDetails && this.state.error && (
              <details className="error-boundary__details">
                <summary>Error details</summary>
                <pre className="error-boundary__error-message">
                  {this.state.error.message}
                </pre>
                {this.state.errorInfo && (
                  <pre className="error-boundary__stack-trace">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </details>
            )}

            <div className="error-boundary__actions">
              <Button variant="primary"
                className="error-boundary__button"
                onClick={this.handleReset}
              >
                Try Again
              </Button>
              {showRefreshButton && (
                <Button variant="secondary"
                  className="error-boundary__button error-boundary__button--secondary"
                  onClick={() => window.location.reload()}
                >
                  Refresh Page
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
